import React, { useState, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import './ReceiptPopup.css';
import { formatPrice, getCurrencySymbol } from '../utils/formatPrice';
import { OrderProduct, Order, OrderCombination, ProstayGuestDto } from '../interfaces';
import { Seat, SeatMealPlanResult } from '../interfaces/mealPlan';
import SplitBillView from './split-bill/SplitBillView';
import { useAuth } from '../AuthContext';
import { getSeatToPay } from '../utils/splitTotals';

interface ReceiptPopupProps {
    isOpen: boolean;
    orderProducts: OrderProduct[];
    onClose: () => void;
    openCashPayment: (seatTotal?: number, reservationInfo?: { reservationId: string; reservationNumber: string; roomNumber: string; roomWithStatusId: string; seatId?: string; guestName?: string }) => void;
    openCardPayment: (seatTotal?: number, reservationInfo?: { reservationId: string; reservationNumber: string; roomNumber: string; roomWithStatusId: string; seatId?: string; guestName?: string }) => void;
    /** @deprecated legacy "Book on Room" picker — no longer surfaced; charge-to-room runs through onChargeOrderToRoom / onChargeToRoom. */
    onBookOnRoom?: () => void;
    activeOrder: Order | undefined;
    showProductId: boolean;
    seats?: Seat[];
    seatResults?: Record<string, SeatMealPlanResult>;
    onChargeToRoom?: (seat: Seat) => void;
    onCloseZeroOrder?: () => void;
    /** Posts the open check to the attached hotel guest's folio. Returns a posting
     *  reference on success so the drawer can show a confirmation step. */
    onChargeOrderToRoom?: () => Promise<RoomChargeResult | void>;
    /** Finalizes the charge-to-room flow (print + leave) after the user taps Done. */
    onRoomChargeComplete?: (order?: Order) => void;
    /** In-flow Prostay guest search for the "Find guest" step. */
    onSearchGuests?: (term: string) => Promise<ProstayGuestDto[]>;
    /** Attaches a searched in-house guest to the current order as a hotel-guest seat. */
    onAttachHotelGuest?: (guest: ProstayGuestDto) => Promise<void> | void;
    /** Looks up folio balance + credit limit for the credit check. */
    onFetchFolio?: (reservationNumber: string) => Promise<{ balance: number; creditLimit: number } | null>;
    /** Assigns order lines to a seat (used by the split-bill unassigned bucket and charge-to-room). */
    onAssignItemsToSeat?: (seatId: string, scope?: { productIds?: string[]; combinationIds?: string[]; onlyUnassigned?: boolean }) => void | Promise<void>;
}

interface RoomChargeResult {
    ok: boolean;
    reference?: string;
    amount?: number;
    error?: string;
    redirected?: boolean;
    order?: Order;
}


const ReceiptPopup: React.FC<ReceiptPopupProps> = ({ isOpen, orderProducts, onClose, activeOrder, openCashPayment, openCardPayment, showProductId, seats = [], seatResults = {}, onChargeToRoom, onCloseZeroOrder, onChargeOrderToRoom, onRoomChargeComplete, onSearchGuests, onAttachHotelGuest, onFetchFolio, onAssignItemsToSeat }) => {
    const { hasPerm } = useAuth();
    const can = (key: string) => !hasPerm || hasPerm(key);
    const hasSplitBillData = seats.length > 0;
    const hasPartialPayments = (activeOrder?.payments?.length ?? 0) > 0 && activeOrder?.isOpen;
    const hasMultipleHotelGuests = seats.filter(s => s.type === 'hotel_guest').length > 1;
    // Whether per-guest payment is available at all. This gates the split view
    // itself, not just its switch: previously `pos-app.payment.split` guarded only
    // the toggle, so the permission neither granted nor denied the feature —
    // someone without it could still split whenever the view happened to default
    // open, and could never split when it didn't.
    const canSplitBill = hasSplitBillData && can('pos-app.payment.split');
    const [showSplitBill, setShowSplitBill] = useState(hasSplitBillData && (!!hasPartialPayments || hasMultipleHotelGuests));
    // Single source of truth for which of the two mutually exclusive views is on
    // screen. `showSplitBill` alone is not enough: it can be true for a user who
    // isn't allowed to split, which would hide the per-guest view AND the normal
    // one-bill summary, leaving the panel empty.
    const splitBillVisible = canSplitBill && showSplitBill;
    // Charge-to-Room flow: Find guest → Review & route → Posted.
    const [roomReview, setRoomReview] = useState(false);
    const [roomStep, setRoomStep] = useState<'select' | 'review' | 'posted'>('review');
    const [guestQuery, setGuestQuery] = useState('');
    const [guestResults, setGuestResults] = useState<ProstayGuestDto[]>([]);
    const [searchingGuests, setSearchingGuests] = useState(false);
    const [verified, setVerified] = useState(false);
    const [posting, setPosting] = useState(false);
    const [postError, setPostError] = useState<string | null>(null);
    const [postedInfo, setPostedInfo] = useState<{ reference: string; amount: number; newBalance: number; room: string; guest: string; order?: Order } | null>(null);
    const [folio, setFolio] = useState<{ balance: number; creditLimit: number } | null>(null);
    useEffect(() => {
        if (!isOpen) {
            setRoomReview(false);
            setRoomStep('review');
            setGuestQuery('');
            setGuestResults([]);
            setVerified(false);
            setPostError(null);
            setPostedInfo(null);
            setFolio(null);
        }
    }, [isOpen]);

    const runGuestSearch = async (term: string) => {
        setGuestQuery(term);
        if (!onSearchGuests) return;
        setSearchingGuests(true);
        try {
            const res = await onSearchGuests(term);
            setGuestResults(res);
        } finally {
            setSearchingGuests(false);
        }
    };

    const openRoomCharge = () => {
        setRoomReview(true);
        setVerified(false);
        setPostError(null);
        setPostedInfo(null);
        setFolio(null);
        const hasHotelGuest = seats.some((s) => s.type === 'hotel_guest' && s.roomNumber) || !!activeOrder?.roomNumber;
        if (hasHotelGuest) {
            setRoomStep('review');
            const resNum = seats.find((s) => s.type === 'hotel_guest' && s.reservationNumber)?.reservationNumber || activeOrder?.prostayReservationNumber;
            if (onFetchFolio && resNum) onFetchFolio(resNum).then(setFolio);
        } else {
            setRoomStep('select');
            runGuestSearch('');
        }
    };

    const pickGuest = async (g: ProstayGuestDto) => {
        if (onAttachHotelGuest) {
            setSearchingGuests(true);
            try { await onAttachHotelGuest(g); } finally { setSearchingGuests(false); }
        }
        setVerified(false);
        if (onFetchFolio && g.reservationNumber) onFetchFolio(g.reservationNumber).then(setFolio);
        setRoomStep('review');
    };

    // Auto-show split bill view when reopening after a partial payment or with multiple hotel guests
    useEffect(() => {
        if (isOpen && hasSplitBillData && (hasPartialPayments || hasMultipleHotelGuests)) {
            setShowSplitBill(true);
        }
    }, [isOpen, hasSplitBillData, hasPartialPayments, hasMultipleHotelGuests]);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getBalance = () => {
        const total = getTotal();
        if (activeOrder && activeOrder.payments) {
            const totalPayments = activeOrder.payments.reduce((sum, payment) => sum + payment.amount, 0);
            const remainingBalance = total - totalPayments;
            return remainingBalance;
        }
        else if (activeOrder) {
            return total;
        }
    };

    const getTotalPaid = () => {
        if (activeOrder) {
            if (!activeOrder.payments || activeOrder.payments.length === 0) {
                return "";
            }

            const totalPaid = activeOrder.payments.reduce((sum, payment) => sum + payment.amount, 0);

            return `${totalPaid}`;
        }
    };

    const getTotalPaidType = () => {
        if (activeOrder) {
            if (!activeOrder.payments || activeOrder.payments.length === 0) {
                return "";
            }

            const paymentTypes = [...new Set(activeOrder.payments.map((payment) => payment.type))];

            const paymentTypeString = paymentTypes.join(" + ");

            return `(${paymentTypeString})`;
        }
    };


    useEffect(() => {
        const handleResize = () => {
            console.log("onresize: innerWidth: " + window.innerWidth + "innerHeight: " + innerHeight)
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);

    const getCombinationPriceValue = (combination: OrderCombination) => {

        let totalPrice = combination.price;

        if (combination.discount && combination.discount > 0) {
            if (combination.discountType == "amount") {
                totalPrice = totalPrice - combination.discount;
            }
            else if (combination.discountType == "percentage") {
                totalPrice = totalPrice - ((combination.discount / 100) * totalPrice);
            }
        }

        return totalPrice;
    };

    const getSubtotal = () => {
        const productsSubtotal = orderProducts.reduce((subtotal, ordProduct) => {
            // Skip meal plan included items
            const isIncluded = (ordProduct as any).mealPlanStatus === 'included' ||
                (ordProduct.seatId && seatResults[ordProduct.seatId]?.evaluations?.find(e => e.orderProductId === ordProduct.id)?.status === 'included');
            if (isIncluded) return subtotal;
            return subtotal + (getProductPriceValue(ordProduct) * ordProduct.qty);
        }, 0);

        const combinationsSubtotal = (activeOrder?.combinations || []).reduce((subtotal, combination) => {
            if ((combination as any).mealPlanStatus === 'included') return subtotal;
            return subtotal + getCombinationPriceValue(combination);
        }, 0);

        return productsSubtotal + combinationsSubtotal;
    }

    const getFormattedSubtotal = () => {
        const total = getSubtotal();
        return total.toFixed(2);
    }

    const getFormattedTotal = () => {
        const total = getTotal();
        return total.toFixed(2);
    }

    const getProductPriceValue = (orderProduct: OrderProduct) => {

        // A selected variant's price REPLACES the base product price (not additive).
        const pricedVariant = orderProduct.variants?.find(v => v?.priceWithoutTax != null && v.priceWithoutTax !== 0);
        let totalPrice = pricedVariant?.priceWithoutTax ?? orderProduct.price;
        if (orderProduct.discount && orderProduct.discount > 0) {
            if (orderProduct.discountType == "amount") {
                totalPrice = totalPrice - orderProduct.discount;
            }
            else if (orderProduct.discountType == "percentage") {
                totalPrice = totalPrice - ((orderProduct.discount / 100) * totalPrice);
            }
        }

        orderProduct.addons.forEach(addon => {
            if (addon.addon.priceWithoutTax) {
                totalPrice = totalPrice + addon.addon.priceWithoutTax * addon.qty;
            }
        });
        return totalPrice;
    };

    const getTax = () => {
        const productsTax = orderProducts.reduce((totalTax, ordProduct) => {
            if (!ordProduct.product) return totalTax;
            const isIncluded = (ordProduct as any).mealPlanStatus === 'included' ||
                (ordProduct.seatId && seatResults[ordProduct.seatId]?.evaluations?.find(e => e.orderProductId === ordProduct.id)?.status === 'included');
            if (isIncluded) return totalTax;

            const { taxRate, taxType, price } = ordProduct.product;
            let taxAmount = taxType === 'percentage'
                ? (taxRate / 100) * price * ordProduct.qty
                : taxRate * ordProduct.qty;
            if (taxType === 'percentage') {
                taxAmount = parseFloat(taxAmount.toFixed(2));
            }
            return totalTax + taxAmount;
        }, 0);

        const combinationsTax = activeOrder?.combinations?.reduce((totalComboTax, combination) => {
            const { taxRate, taxType, price } = combination;
            let comboTaxAmount = taxType === 'percentage'
                ? (taxRate / 100) * price
                : taxRate;
            if (taxType === 'percentage') {
                comboTaxAmount = parseFloat(comboTaxAmount.toFixed(2));
            }
            return totalComboTax + comboTaxAmount;
        }, 0) || 0;

        return productsTax + combinationsTax;
    };



    const getTotal = () => {
        const subtotal = getSubtotal();
        const discount = getOrderDiscount() || 0;
        const tax = getTax();
        return subtotal + tax - discount;
    }

    const getDiscountValue = (orderProduct: OrderProduct) => {
        if (orderProduct.discount && orderProduct.discount != 0) {
            const productTotalPrice = calculateProductTotalPrice(orderProduct);
            if (orderProduct.discountType == "amount")
                return orderProduct.discount;
            else if (orderProduct.discountType == "percentage")
                return (orderProduct.discount / 100) * productTotalPrice;
        }
        return 0;
    }

    const getOrderDiscount = () => {
        if (activeOrder) {
            let discount = 0;
            if (activeOrder.orderDiscountType == "percentage") {
                const subtotal = getSubtotal();
                discount = (activeOrder.orderDiscountValue / 100) * subtotal;
                discount = parseFloat(discount.toFixed(2));
                return discount;
            }
            else if (activeOrder.orderDiscountType == "amount") {
                return activeOrder.orderDiscountValue;
            }
            return discount;
        }
    }

    const getDiscountValueFormatted = (orderProduct: OrderProduct) => {
        const discountValue = getDiscountValue(orderProduct);
        return discountValue.toFixed(2);
    };

    // Display unit price: a selected variant's price REPLACES the product base price
    // (addons render as their own lines, so they are NOT included here).
    const getDisplayUnitPrice = (op: OrderProduct) => {
        const pv = op.variants?.find(v => v?.priceWithoutTax != null && v.priceWithoutTax !== 0);
        return pv?.priceWithoutTax ?? op.product.price;
    };

    const calculateProductTotalPrice = (orderProduct: OrderProduct) => {
        // A selected variant's price REPLACES the base product price (not additive).
        const pricedVariant = orderProduct.variants?.find(v => v?.priceWithoutTax != null && v.priceWithoutTax !== 0);
        let totalPrice = pricedVariant?.priceWithoutTax ?? orderProduct.product.price;

        orderProduct.addons.forEach(addon => {
            if (addon.addon.priceWithoutTax) {
                totalPrice += addon.addon.priceWithoutTax * addon.qty;
            }
        });

        return totalPrice;
    };

    // const getProductPrice = (orderProduct: OrderProduct) => {
    //     let totalPrice = orderProduct.product.price;

    //     // orderProduct.variants.forEach(variant => {
    //     //     if (variant.priceWithoutTax) {
    //     //         totalPrice += variant.priceWithoutTax;
    //     //     }
    //     // });

    //     orderProduct.addons.forEach(addon => {
    //         if (addon.addon.priceWithoutTax) {
    //             totalPrice += addon.addon.priceWithoutTax * addon.qty;
    //         }
    //     });

    //     return totalPrice.toFixed(2);
    // };

    const styles = StyleSheet.create({
        drawer: {
            top: 0,
            right: 0,
            width: windowSize.width > 900 ? windowSize.width * 0.47 : windowSize.width * 0.9,
            height: windowSize.height - 55,
            backgroundColor: 'white',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: '#D0D5DD',
            elevation: 5,
            zIndex: 1002,
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
        },
        openDrawer: {
            right: 0,
        },
        closedDrawer: {
            right: -(windowSize.width > 900 ? windowSize.width * 0.47 : windowSize.width * 0.9),
        },
    });

    const formatReceiptDate = (d?: Date) => {
        const date = d ? new Date(d) : new Date();
        if (isNaN(date.getTime())) return '';
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${String(date.getFullYear()).slice(2)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const getCombinationDiscountValue = (orderCombination: OrderCombination) => {
        if (orderCombination.discount && orderCombination.discount != 0) {
            const productTotalPrice = orderCombination.price;
            if (orderCombination.discountType == "amount")
                return orderCombination.discount;
            else if (orderCombination.discountType == "percentage")
                return (orderCombination.discount / 100) * productTotalPrice;
        }
        return 0;
    }

    const getCombinationDiscountValueFormatted = (orderProduct: OrderCombination) => {
        const discountValue = getCombinationDiscountValue(orderProduct);
        return discountValue.toFixed(2);
    };

    // ── Charge-to-Room review data ──────────────────────────────────────────
    const hotelGuestSeats = seats.filter((s) => s.type === 'hotel_guest' && s.roomNumber);
    const roomChargeTargets = hotelGuestSeats.length > 0
        ? hotelGuestSeats.map((s) => ({
            key: s.id,
            name: s.guestName || 'Hotel guest',
            room: s.roomNumber || '',
            reservation: s.reservationNumber || '',
            rate: s.rateName || '',
            res: seatResults[s.id] as SeatMealPlanResult | undefined,
        }))
        : (activeOrder?.roomNumber
            ? [{
                key: 'order',
                name: activeOrder.customerName || 'Hotel guest',
                room: activeOrder.roomNumber || '',
                reservation: activeOrder.prostayReservationNumber || '',
                rate: '',
                res: undefined as SeatMealPlanResult | undefined,
            }]
            : []);
    const roomChargeAmount = (() => {
        const bal = getBalance();
        return bal != null ? bal : getTotal();
    })();
    const roomReviewIncluded = roomChargeTargets.reduce((s, t) => s + (t.res?.totalIncluded || 0), 0);
    const roomCheckTotal = roomChargeAmount + roomReviewIncluded;
    const overLimit = folio && folio.creditLimit > 0 ? (folio.balance + roomChargeAmount) > folio.creditLimit : false;
    // Single hotel-guest target with a real seat → we can assign unassigned lines to it
    // so the meal-plan allowance applies before posting.
    const roomAssignSeatId = roomChargeTargets.length === 1 && roomChargeTargets[0].key !== 'order' ? roomChargeTargets[0].key : null;
    const roomUnassignedCount = orderProducts.filter((p) => !p.seatId).length + (activeOrder?.combinations || []).filter((c) => !c.seatId).length;
    const roomItemQty = orderProducts.reduce((n, p) => n + (p.qty || 1), 0) + (activeOrder?.combinations?.length || 0);
    const roomAlreadyPaid = Math.max(0, getTotal() - roomChargeAmount);
    const roomGuestName = roomChargeTargets[0]?.name || '';
    const roomGuestFirst = roomGuestName.split(' ')[0] || 'this guest';
    const roomNumberStr = roomChargeTargets[0]?.room || '';

    const postRoomCharge = async () => {
        if (!onChargeOrderToRoom || posting) return;
        setPosting(true);
        setPostError(null);
        const res = await onChargeOrderToRoom();
        setPosting(false);
        if (!res || res.redirected) return; // multi-guest path handed off to "who pays"
        if (res.ok) {
            const amount = res.amount ?? roomChargeAmount;
            const target = roomChargeTargets[0];
            setPostedInfo({
                reference: res.reference || '',
                amount,
                newBalance: (folio?.balance ?? 0) + amount,
                room: target?.room || '',
                guest: target?.name || '',
                order: res.order,
            });
            setRoomStep('posted');
        } else {
            setPostError(res.error || 'Failed to post to room.');
        }
    };

    return (
      <>
        <div className={`rcpt-scrim${isOpen ? ' is-open' : ''}`} onClick={onClose} />
        <div className={`rcpt ps-scope${isOpen ? ' is-open' : ''}`} style={{ ...styles.drawer, ...(isOpen ? styles.openDrawer : styles.closedDrawer) }}>
            <div className='rcpt__body'>
                <div className='rcpt__head d-flex px-2 py-3 justify-content-between' style={{ borderBottom: '1px solid #DEE3EB' }}>
                    <h6 style={{ margin: '0' }}>Receipt</h6>
                    <button className='text-btn p-0 m-0 d-flex justify-content-center' onClick={onClose}>
                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 1L1 13M1 1L13 13" stroke="#98A2B3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </button>
                </div>
                {roomReview && (
                    <div className='rcr p-3'>
                        <div className='rcr__head'>
                            <span className='rcr__icon'>
                                <svg width="20" height="20" viewBox="0 0 30 22" fill="none"><path d="M3.28 10.53V2.56c0-.5.4-.94.94-.94h21.56c.52 0 .94.42.94.94v7.97M1.4 16.16v-3.75c0-.52.42-.94.94-.94h25.32c.52 0 .94.42.94.94v3.75M1.4 16.16h27.2M3.5 18.97l-.4 1.97M26.5 18.97l.4 1.97M5.15 10.53V8.66c0-.52.42-.94.94-.94h6.56c.52 0 .94.42.94.94v1.87M16.4 10.53V8.66c0-.52.42-.94.94-.94h6.56c.52 0 .94.42.94.94v1.87" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </span>
                            <div className='rcr__heading'>
                                <h6 className='rcr__title'>Charge to Room</h6>
                                <span className='rcr__sub'>Post this check to the guest folio in Prostay</span>
                            </div>
                        </div>

                        {/* Stepper */}
                        <div className='rcr__steps'>
                            {['Find guest', 'Review & route', 'Posted'].map((label, i) => {
                                const idx = ['select', 'review', 'posted'].indexOf(roomStep);
                                return (
                                    <div key={label} className={`rcr__step${idx >= i ? ' is-active' : ''}${idx > i ? ' is-done' : ''}`}>
                                        <span className='rcr__stepn'>{idx > i ? '✓' : i + 1}</span>
                                        <span className='rcr__stepl'>{label}</span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* STEP 1 — Find guest */}
                        {roomStep === 'select' && (
                            <div className='rcr__pane'>
                                <div className='rcr__search'>
                                    <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M16.5 16.5L12.88 12.88M12.88 12.88A6.67 6.67 0 1 0 3.45 3.45a6.67 6.67 0 0 0 9.43 9.43Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    <input autoFocus value={guestQuery} onChange={(e) => runGuestSearch(e.target.value)} placeholder="Search room number, name or reservation…" />
                                </div>
                                <div className='rcr__results'>
                                    {searchingGuests && <div className='rcr__searching'>Searching…</div>}
                                    {!searchingGuests && guestResults.map((g) => (
                                        <button key={g.id} className='rcr__result' onClick={() => pickGuest(g)}>
                                            <span className='rcr__resroom'>{g.roomNumber || '—'}</span>
                                            <span className='rcr__resinfo'>
                                                <span className='rcr__resname'>{`${g.firstName || ''} ${g.lastName || ''}`.trim()}</span>
                                                <span className='rcr__resres'>{g.reservationNumber}{g.rateName ? ' · ' + g.rateName : ''}</span>
                                            </span>
                                            <span className='rcr__resstatus'>● In House</span>
                                        </button>
                                    ))}
                                    {!searchingGuests && guestResults.length === 0 && (
                                        <div className='rcr__empty'>No in-house guest matches “{guestQuery}”.</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* STEP 2 — Review & route */}
                        {roomStep === 'review' && (
                            <div className='rcr__pane'>
                                {roomChargeTargets.length === 0 ? (
                                    <div className='rcr__empty'>
                                        No hotel guest is attached. <button className='rcr__link' onClick={() => { setRoomStep('select'); runGuestSearch(''); }}>Find a guest</button> to charge this check to a room.
                                    </div>
                                ) : (
                                    <>
                                        <p className='rcr__intro'>
                                            Nothing is collected now. The balance below is added to {roomChargeTargets.length > 1 ? 'the guests’ room folios' : `${roomGuestFirst}’s room folio`} and settled at check‑out.
                                        </p>

                                        <div className='rcr__sectionlabel'>Charging to</div>
                                        {roomChargeTargets.map((t) => (
                                            <div className='rcr__guest' key={t.key}>
                                                <div className='rcr__guesttop'>
                                                    <div className='rcr__who'>
                                                        <span className='rcr__name'>{t.name}</span>
                                                        {t.reservation && <span className='rcr__res'>Res. {t.reservation}</span>}
                                                    </div>
                                                    <span className='rcr__room'>Room {t.room}</span>
                                                </div>
                                                {(t.rate || t.res) && (
                                                    <div className='rcr__plan'>
                                                        {t.rate && <span className='rcr__rate'>{t.rate}</span>}
                                                        {t.res && t.res.currentMealPeriod && (
                                                            <span className='rcr__period'>{t.res.currentMealPeriod}</span>
                                                        )}
                                                    </div>
                                                )}
                                                {t.res && t.res.isAllowanceAlreadyUsed && (
                                                    <div className='rcr__warn'>Meal-plan allowance already used today — extras will be charged.</div>
                                                )}
                                                {onAttachHotelGuest && roomChargeTargets.length === 1 && (
                                                    <button className='rcr__change' onClick={() => { setRoomStep('select'); runGuestSearch(''); }}>Change guest</button>
                                                )}
                                            </div>
                                        ))}

                                        {folio && (
                                            <div className='rcr__fin'>
                                                <div><span>Current folio balance</span><b>{formatPrice(folio.balance)}</b></div>
                                                <div><span>Credit limit</span><b>{formatPrice(folio.creditLimit)}</b></div>
                                            </div>
                                        )}

                                        {roomChargeTargets.length > 1 && (
                                            <div className='rcr__note'>{roomChargeTargets.length} hotel guests on this check — you'll choose how to split across rooms next.</div>
                                        )}

                                        <div className='rcr__sectionlabel'>What you’re charging</div>
                                        <div className='rcr__route'>
                                            <div className='rcr__routerow'>
                                                <span>{roomItemQty} item{roomItemQty !== 1 ? 's' : ''} on {activeOrder?.tableName || 'this check'}</span>
                                                <span>{formatPrice(roomCheckTotal)}</span>
                                            </div>
                                            {roomReviewIncluded > 0 && (
                                                <div className='rcr__routerow rcr__routerow--credit'>
                                                    <span>Covered by meal plan</span>
                                                    <span>−{formatPrice(roomReviewIncluded)}</span>
                                                </div>
                                            )}
                                            {roomAlreadyPaid > 0 && (
                                                <div className='rcr__routerow'>
                                                    <span>Already paid</span>
                                                    <span>−{formatPrice(roomAlreadyPaid)}</span>
                                                </div>
                                            )}
                                            <div className='rcr__routerow rcr__routerow--room'>
                                                <span>{roomChargeTargets.length > 1 ? 'Posting to rooms' : `Posting to Room ${roomNumberStr}`}</span>
                                                <span>{formatPrice(roomChargeAmount)}</span>
                                            </div>
                                        </div>

                                        {roomAssignSeatId && roomUnassignedCount > 0 && onAssignItemsToSeat && (
                                            <div className='rcr__assign'>
                                                <div className='rcr__assigntxt'>
                                                    <b>{roomUnassignedCount}</b> item{roomUnassignedCount > 1 ? 's aren’t' : ' isn’t'} linked to {roomGuestFirst}’s meal plan, so {roomUnassignedCount > 1 ? 'they’ll' : 'it’ll'} be charged in full. Link {roomUnassignedCount > 1 ? 'them' : 'it'} to apply the allowance.
                                                </div>
                                                <button
                                                    className='rcr__assignbtn'
                                                    onClick={() => onAssignItemsToSeat(roomAssignSeatId, { onlyUnassigned: true })}
                                                >
                                                    Apply meal plan
                                                </button>
                                            </div>
                                        )}

                                        {overLimit && (
                                            <div className='rcr__warn rcr__warn--limit'>Posting exceeds credit limit — manager approval required.</div>
                                        )}

                                        <label className='rcr__verify'>
                                            <input type='checkbox' checked={verified} onChange={(e) => setVerified(e.target.checked)} />
                                            <span>I confirmed this is {roomGuestFirst}{roomNumberStr ? ` in Room ${roomNumberStr}` : ''} (checked ID / signature).</span>
                                        </label>

                                        {postError && <div className='rcr__warn rcr__warn--limit'>{postError}</div>}
                                    </>
                                )}
                            </div>
                        )}

                        {/* STEP 3 — Posted */}
                        {roomStep === 'posted' && postedInfo && (
                            <div className='rcr__pane rcr__pane--done'>
                                <div className='rcr__doneicon'>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </div>
                                <div className='rcr__donetitle'>{formatPrice(postedInfo.amount)} posted to Room {postedInfo.room}</div>
                                <div className='rcr__donesub'>Added to {postedInfo.guest}’s folio in Prostay.</div>
                                <div className='rcr__donecard'>
                                    <div className='rcr__donerow'><span>Guest</span><b>{postedInfo.guest}</b></div>
                                    <div className='rcr__donerow'><span>Room</span><b>{postedInfo.room}</b></div>
                                    {roomReviewIncluded > 0 && (
                                        <div className='rcr__donerow'><span>Covered by meal plan</span><b>{formatPrice(roomReviewIncluded)}</b></div>
                                    )}
                                    <div className='rcr__donerow'><span>Posted to folio</span><b>{formatPrice(postedInfo.amount)}</b></div>
                                    <div className='rcr__donerow'><span>New folio balance</span><b>{formatPrice(postedInfo.newBalance)}</b></div>
                                    <div className='rcr__donerow'><span>Posting reference</span><b className='rcr__mono'>{postedInfo.reference}</b></div>
                                </div>
                                <div className='rcr__settled'>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    This check is settled — balance due {formatPrice(0)}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className='p-3 rcpt__content' style={{ display: roomReview ? 'none' : undefined }}>
                    <div className='rcpt__ident d-flex py-2 align-items-center justify-content-between'>
                        <span style={{ color: '#1643E0', fontWeight: '600', fontFamily: 'Inter', fontSize: '14px', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeOrder?.tableName}  {activeOrder?.customerName ? "(" + activeOrder?.customerName + (activeOrder?.roomNumber ? " - " + activeOrder?.roomNumber : "") + ")" : ""}</span>
                        <span className='rcpt__date' style={{ color: '#475467', fontWeight: '600', fontFamily: 'Inter', fontSize: '14px' }}>{formatReceiptDate(activeOrder?.date)}</span>
                    </div>

                    {canSplitBill && (
                        <div className='rcpt__splitbar'>
                            <div className='rcpt__splitinfo'>
                                <span className='rcpt__splittitle'>{showSplitBill ? 'Per-guest view' : 'One bill for the table'}</span>
                                <span className='rcpt__splithint'>{showSplitBill ? 'Showing each guest’s items so you can take payment per guest.' : 'Turn on to see and pay each guest’s items separately.'}</span>
                            </div>
                            <button
                                type='button'
                                role='switch'
                                aria-checked={showSplitBill}
                                onClick={() => setShowSplitBill(!showSplitBill)}
                                className={`rcpt__splittoggle${showSplitBill ? ' is-on' : ''}`}
                            >
                                <span className='rcpt__splittoggleknob' />
                                <span className='rcpt__splittogglelabel'>Split by guest</span>
                            </button>
                        </div>
                    )}

                    {/* Split Bill View */}
                    {splitBillVisible && (
                        <div className='rcpt__split'>
                        <SplitBillView
                            order={activeOrder!}
                            seats={seats}
                            products={orderProducts}
                            combinations={activeOrder?.combinations || []}
                            seatResults={seatResults}
                            onAssignUnassigned={onAssignItemsToSeat ? (seatId) => onAssignItemsToSeat(seatId, { onlyUnassigned: true }) : undefined}
                            onPaySeat={(seatId, method) => {
                                const seat = seats.find(s => s.id === seatId);
                                if (!seat) return;
                                // Charge the discounted amount: chargeable base minus this seat's share of the order discount.
                                const toPay = getSeatToPay(seat, activeOrder, seats, orderProducts, activeOrder?.combinations || [], seatResults);

                                const resInfo = seat.type === 'hotel_guest' && seat.reservationId && seat.reservationNumber && seat.roomNumber && seat.roomWithStatusId
                                    ? { reservationId: seat.reservationId, reservationNumber: seat.reservationNumber, roomNumber: seat.roomNumber, roomWithStatusId: seat.roomWithStatusId, seatId: seat.id, guestName: seat.guestName }
                                    : { seatId: seat.id, guestName: seat.guestName } as any;

                                if (method === 'cash') openCashPayment(toPay, resInfo);
                                else if (method === 'card') openCardPayment(toPay, resInfo);
                                else if (method === 'room' && onChargeToRoom) {
                                    onChargeToRoom(seat);
                                }
                            }}
                            onPayForOthers={(_payerSeatId, targetSeatIds, method) => {
                                const payerSeat = seats.find(x => x.id === _payerSeatId);
                                const allSeatIds = [_payerSeatId, ...targetSeatIds];
                                let totalToPay = 0;
                                for (const sid of allSeatIds) {
                                    const s = seats.find(x => x.id === sid);
                                    if (!s) continue;
                                    // Discounted amount per seat, so paying for others also reflects the order discount.
                                    totalToPay += getSeatToPay(s, activeOrder, seats, orderProducts, activeOrder?.combinations || [], seatResults);
                                }
                                const resInfo = payerSeat?.type === 'hotel_guest' && payerSeat.reservationId && payerSeat.reservationNumber && payerSeat.roomNumber && payerSeat.roomWithStatusId
                                    ? { reservationId: payerSeat.reservationId, reservationNumber: payerSeat.reservationNumber, roomNumber: payerSeat.roomNumber, roomWithStatusId: payerSeat.roomWithStatusId, seatId: payerSeat.id, guestName: payerSeat.guestName }
                                    : { seatId: payerSeat?.id, guestName: payerSeat?.guestName } as any;
                                if (method === 'card') openCardPayment(totalToPay, resInfo);
                                else openCashPayment(totalToPay, resInfo);
                            }}
                        />
                        </div>
                    )}
                    {!splitBillVisible && (<><div className='border-box rcpt__summary'>
                        <div className='d-flex justify-content-between gap-0' style={{ borderTop: '1px solid #F4F4F4' }}>
                            <div className='col px-2 py-1' style={{ backgroundColor: '#fff' }}>
                                <span style={{ fontSize: '14px' }}>Subtotal</span>
                            </div>
                            <div className='col-auto px-2 py-1' style={{ backgroundColor: '#fff' }}>
                                <span style={{ fontSize: '14px', textAlign: 'end' }}>{formatPrice(getFormattedSubtotal())}</span>
                            </div>
                        </div>
                        <div className='d-flex justify-content-between gap-0' style={{ borderTop: '1px solid #F4F4F4' }}>
                            <div className='col px-2 py-1' style={{ backgroundColor: '#fff' }}>
                                <span style={{ fontSize: '14px' }}>Tax</span>
                            </div>
                            <div className='col-auto px-2 py-1' style={{ backgroundColor: '#fff' }}>
                                <span style={{ fontSize: '14px', textAlign: 'end' }}>{formatPrice(getTax())}</span>
                            </div>
                        </div>
                        {
                            getOrderDiscount() != 0 &&
                            <div className='d-flex justify-content-between gap-0' style={{ borderTop: '1px solid #F4F4F4' }}>
                                <div className='col px-2 py-1' style={{ backgroundColor: '#fff' }}>
                                    <span style={{ fontSize: '14px' }}>{activeOrder?.orderDiscountName || 'Discount'}</span>
                                </div>
                                <div className='col-auto px-2 py-1' style={{ backgroundColor: '#fff' }}>
                                    <span style={{ fontSize: '14px', textAlign: 'end', whiteSpace: 'nowrap' }}>{`-${formatPrice(getOrderDiscount())}`}</span>
                                </div>
                            </div>
                        }

                        <div className='rcpt__totalrow d-flex justify-content-between gap-0'>
                            <div className='col px-2 py-1' style={{ backgroundColor: '#EAECF0' }}>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#667085' }}>Total</span>
                            </div>
                            <div className='col-auto px-2 py-1' style={{ backgroundColor: '#EAECF0' }}>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#667085', textAlign: 'end' }}>{formatPrice(getFormattedTotal())}</span>
                            </div>
                        </div>
                        {
                            getTotalPaid() != "" &&
                            <div className='d-flex justify-content-between gap-0'>
                                <div className='col px-2 py-1' style={{ backgroundColor: '#EAECF0' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#667085' }}>Total Paid{getTotalPaidType()}</span>
                                </div>
                                <div className='col-auto px-2 py-1' style={{ backgroundColor: '#EAECF0' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#667085', textAlign: 'end' }}>{formatPrice(getTotalPaid())}</span>
                                </div>
                            </div>
                        }
                        <div className='d-flex justify-content-between gap-0'>
                            <div className='col px-2 py-1' style={{ backgroundColor: '#EAECF0' }}>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#667085' }}>Balance Due</span>
                            </div>
                            <div className='col-auto px-2 py-1' style={{ backgroundColor: '#EAECF0' }}>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#667085', textAlign: 'end' }}>{formatPrice(getBalance())}</span>
                            </div>
                        </div>
                    </div>

                    <div className='border-box rcpt__items mt-3'>
                        <div className='rcpt__cols d-flex gap-0'>
                            {
                                showProductId &&
                                <div className='col px-2 py-1' style={{ backgroundColor: '#F9FAFB' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '500' }}>ID</span>
                                </div>
                            }


                            <div className='col px-2 py-1' style={{ backgroundColor: '#F9FAFB' }}>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>QTY</span>
                            </div>
                            <div className='col-6 px-2 py-1 ' style={{ backgroundColor: '#F9FAFB' }}>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>Product Name</span>
                            </div>
                            <div className='col px-2 py-1 d-flex justify-content-end' style={{ backgroundColor: '#F9FAFB' }}>
                                <span style={{ fontSize: '14px', fontWeight: '500' }}>Price</span>
                            </div>
                        </div>
                        <div className="hide-scrollbar rcpt__scroll">
                            {
                                orderProducts && orderProducts.length > 0 && (() => {
                                    // ── Build display groups (mirrors TableOrder logic) ──
                                    interface RcptGroup {
                                        rep: typeof orderProducts[number];
                                        qty: number;
                                        ids: string[];
                                        isIncludedInMealPlan: boolean;
                                    }
                                    const groups: RcptGroup[] = [];
                                    orderProducts.forEach((p) => {
                                        let status: 'included' | 'extra' | 'none' = 'none';
                                        if ((p as any).mealPlanStatus === 'included') status = 'included';
                                        else if ((p as any).mealPlanStatus === 'extra') status = 'extra';
                                        if (p.seatId && seatResults[p.seatId]) {
                                            const ev = seatResults[p.seatId].evaluations?.find(e => e.orderProductId === p.id);
                                            if (ev) status = ev.status;
                                        }
                                        const isWaived = !!(p as any).mealPlanOverride?.waived;
                                        const visualIncluded = status === 'included' || isWaived;
                                        const visualStatus: 'included' | 'extra' | 'none' = visualIncluded ? 'included' : status;

                                        const matchIdx = groups.findIndex(g => {
                                            const r = g.rep;
                                            if (r.productId !== p.productId) return false;
                                            if ((r.seatId || undefined) !== (p.seatId || undefined)) return false;
                                            if (!!r.sentToKitchen !== !!p.sentToKitchen) return false;
                                            if ((r.note || '') !== (p.note || '')) return false;
                                            if ((r.addons?.length || 0) > 0 || (p.addons?.length || 0) > 0) return false;
                                            if ((r.variants?.length || 0) > 0 || (p.variants?.length || 0) > 0) return false;
                                            if ((r.discount || 0) !== (p.discount || 0)) return false;
                                            if ((r.discountType || '') !== (p.discountType || '')) return false;
                                            if (g.isIncludedInMealPlan !== (visualStatus === 'included')) return false;
                                            return true;
                                        });
                                        if (matchIdx >= 0) {
                                            groups[matchIdx].qty += p.qty || 1;
                                            groups[matchIdx].ids.push(p.id);
                                        } else {
                                            groups.push({ rep: p, qty: p.qty || 1, ids: [p.id], isIncludedInMealPlan: visualStatus === 'included' });
                                        }
                                    });
                                    return (
                                <>
                                    {groups.map((group, index) => {
                                        const prod = { ...group.rep, qty: group.qty } as typeof group.rep;
                                        const isIncludedInMealPlan = group.isIncludedInMealPlan;
                                        return (
                                        <div key={index} className='d-flex gap-0' style={{ borderTop: '1px solid #EAECF0', }}>
                                            {
                                                showProductId &&
                                                <div className='col px-2 py-1' style={{ backgroundColor: index % 2 == 1 ? '#F9FAFB' : '#ffffff' }}>
                                                    <span style={{ fontSize: '14px' }}>{prod.product.plu}</span>
                                                </div>
                                            }

                                            <div className='col px-2 py-1' style={{ backgroundColor: index % 2 == 1 ? '#F9FAFB' : '#ffffff' }}>
                                                <span style={{ fontSize: '14px' }}>{prod.qty}</span>
                                            </div>
                                            <div className='col-6 px-2 py-1 ' style={{ backgroundColor: index % 2 == 1 ? '#F9FAFB' : '#ffffff' }}>
                                                <div className='d-flex align-items-center gap-1'>
                                                    <span style={{ fontSize: '14px', fontWeight: '500' }}>{prod.name}</span>
                                                    {isIncludedInMealPlan && (
                                                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8, backgroundColor: '#ECFDF3', color: '#027A48' }}>Meal Plan</span>
                                                    )}
                                                </div>
                                                {
                                                    prod.addons && prod.addons.length > 0 &&
                                                    <>
                                                        <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>
                                                            Add-ons:
                                                        </span>
                                                        {
                                                            prod.addons.map(addon =>
                                                                <>
                                                                    {addon.qty > 1 ?
                                                                        <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>
                                                                            {addon.qty} x {addon.addon.name}
                                                                        </span>
                                                                        :
                                                                        <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>
                                                                            {addon.addon.name}
                                                                        </span>
                                                                    }

                                                                </>
                                                            )
                                                        }
                                                    </>
                                                }
                                                {
                                                    prod.variants && prod.variants.length > 0 &&
                                                    <>
                                                        <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>
                                                            Variants:
                                                        </span>
                                                        {
                                                            prod.variants.map(variant =>
                                                                <>
                                                                    <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>
                                                                        {variant.name}
                                                                    </span>
                                                                </>
                                                            )
                                                        }
                                                    </>
                                                }
                                                {
                                                    getDiscountValue(prod) != 0 &&
                                                    <>
                                                        <span style={{ fontSize: '14px', display: 'flex' }}>
                                                            {prod.discountName || 'Discount'}
                                                        </span>
                                                    </>
                                                }
                                                {
                                                    prod.note != '' &&
                                                    <>
                                                        <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>Note: {prod.note}</span>
                                                    </>
                                                }
                                            </div>
                                            <div className='col px-2 py-1 d-flex flex-column' style={{ backgroundColor: index % 2 == 1 ? '#F9FAFB' : '#ffffff' }}>
                                                <span style={{ fontSize: '14px', textAlign: 'end', color: isIncludedInMealPlan ? '#027A48' : undefined }}>
                                                    {isIncludedInMealPlan ? formatPrice(0) : formatPrice((getDiscountValue(prod) > 0 ? getDisplayUnitPrice(prod) - getDiscountValue(prod) : getDisplayUnitPrice(prod)) * (prod.qty || 1))}
                                                </span>
                                                {
                                                    prod.addons && prod.addons.length > 0 &&
                                                    <>
                                                        <span style={{ fontSize: '14px', textAlign: 'end', color: 'transparent' }}>
                                                            Add-ons:
                                                        </span>
                                                        {
                                                            prod.addons.map(addon =>
                                                                <>
                                                                    {
                                                                        addon.addon.priceWithoutTax && addon.addon.priceWithoutTax != 0 ?
                                                                            <span style={{ fontSize: '14px', textAlign: 'end' }}>{formatPrice(addon.addon.priceWithoutTax * addon.qty)}</span>
                                                                            :
                                                                            <span style={{ fontSize: '14px', color: 'transparent' }}>{getCurrencySymbol()} </span>
                                                                    }
                                                                </>
                                                            )
                                                        }
                                                    </>
                                                }
                                                {
                                                    getDiscountValue(prod) != 0 &&
                                                    <span style={{ fontSize: '14px', textAlign: 'end', whiteSpace: 'nowrap' }}>{`-${formatPrice(getDiscountValueFormatted(prod))}`}</span>
                                                }
                                            </div>
                                        </div>
                                        );
                                    })}
                                </>
                                    );
                                })()
                            }

                            {
                                activeOrder?.combinations && activeOrder?.combinations.length > 0 &&
                                <>
                                    {activeOrder?.combinations.map((comb, index) => (
                                        <div className='d-flex gap-0' style={{ borderTop: '1px solid #EAECF0', }}>
                                            {
                                                showProductId &&
                                                <div className='col px-2 py-1' style={{ backgroundColor: index % 2 == 1 ? '#F9FAFB' : '#ffffff' }}>
                                                    <span style={{ fontSize: '14px' }}>{comb.combinationPlu}</span>
                                                </div>
                                            }

                                            <div className='col px-2 py-1' style={{ backgroundColor: index % 2 == 1 ? '#F9FAFB' : '#ffffff' }}>
                                                <span style={{ fontSize: '14px' }}>1</span>
                                            </div>
                                            <div className='col-6 px-2 py-1 ' style={{ backgroundColor: index % 2 == 1 ? '#F9FAFB' : '#ffffff' }}>
                                                <span style={{ fontSize: '14px', fontWeight: '500' }}>{comb.combinationName}</span>
                                                {comb.selectedsubproductsNames && (
                                                    <>
                                                        {comb.selectedsubproductsNames.map((subProd, idx) => (
                                                            <span
                                                                key={idx}
                                                                style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}
                                                            >
                                                                {subProd}
                                                            </span>
                                                        ))}
                                                    </>
                                                )}
                                                {
                                                    getCombinationDiscountValue(comb) != 0 &&
                                                    <>
                                                        <span style={{ fontSize: '14px', display: 'flex' }}>
                                                            {comb.discountName || 'Discount'}
                                                        </span>
                                                    </>
                                                }
                                                {
                                                    comb.note != '' && comb.note != null &&
                                                    <>
                                                        <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex' }}>Note: {comb.note}</span>
                                                    </>
                                                }
                                            </div>
                                            <div className='col px-2 py-1 d-flex flex-column' style={{ backgroundColor: index % 2 == 1 ? '#F9FAFB' : '#ffffff' }}>
                                                <span style={{ fontSize: '14px', textAlign: 'end' }}>{formatPrice(comb.price)}</span>
                                                {comb.selectedsubproductsNames && (
                                                    <>
                                                        {comb.selectedsubproductsNames.map((subProd, idx) => (
                                                            <span
                                                                key={idx}
                                                                style={{ fontSize: '14px', textAlign: 'end', display: 'flex', color: 'transparent' }}
                                                            >
                                                                .
                                                            </span>
                                                        ))}
                                                    </>
                                                )}
                                                {
                                                    getCombinationDiscountValue(comb) != 0 &&
                                                    <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex', justifyContent: 'end', whiteSpace: 'nowrap' }}>{`-${formatPrice(getCombinationDiscountValueFormatted(comb))}`}</span>
                                                }
                                            </div>
                                        </div>
                                    ))}
                                </>
                            }
                        </div>
                    </div>
                    </>)}
                </div>
            </div>


            {roomReview ? (
                <div className='rcpt__foot rcr__foot d-flex align-items-center gap-2 p-3' style={{ borderTop: '1px solid #F4F4F4' }}>
                    {roomStep === 'posted' ? (
                        <button
                            className='rcr__confirm'
                            onClick={() => { onRoomChargeComplete ? onRoomChargeComplete(postedInfo?.order) : (setRoomReview(false), onClose()); }}
                        >
                            Done
                        </button>
                    ) : (
                        <>
                            <button className='rcr__cancel' onClick={() => setRoomReview(false)}>Back</button>
                            <button
                                className='rcr__confirm'
                                disabled={roomStep !== 'review' || roomChargeTargets.length === 0 || !verified || posting}
                                onClick={postRoomCharge}
                            >
                                {posting
                                    ? 'Posting…'
                                    : roomChargeTargets.length > 1
                                        ? `Post ${formatPrice(roomChargeAmount)} to rooms`
                                        : `Post ${formatPrice(roomChargeAmount)} to Room ${roomChargeTargets[0]?.room || ''}`}
                            </button>
                        </>
                    )}
                </div>
            ) : (
            <div className='rcpt__foot rcpt__pay p-3' style={{ borderTop: '1px solid #F4F4F4' }}>
                <div className='rcpt__paylabel'>Take payment · {formatPrice(getBalance())} due</div>
                <div className='rcpt__payrow d-flex align-items-stretch gap-2 justify-content-between hide-scrollbar' style={{ overflowX: 'auto' }}>
                {getTotal() === 0 && onCloseZeroOrder && can('pos-app.payment.mark-paid') && (
                    <div className='col'>
                        <button className='large-blue-outline-sq-btn w-100' onClick={onCloseZeroOrder} style={{ backgroundColor: '#027A48', borderColor: '#027A48', color: '#fff' }}>
                            <svg width="22" height="22" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '5px' }}>
                                <path d="M26.5 12.8572V14.0072C26.4985 16.7027 25.6256 19.3255 24.0117 21.4844C22.3977 23.6434 20.1291 25.2228 17.5442 25.987C14.9593 26.7513 12.1966 26.6595 9.66809 25.7254C7.1396 24.7912 4.98082 23.0648 3.5137 20.8035C2.04658 18.5422 1.34974 15.8672 1.5271 13.1775C1.70445 10.4879 2.74651 7.92756 4.49785 5.8785C6.24919 3.82943 8.61598 2.40138 11.2452 1.80734C13.8745 1.21329 16.6253 1.48508 19.0875 2.58216M26.5 4L14 16.5125L10.25 12.7625" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Close Order (Meal Plan)
                        </button>
                    </div>
                )}
                {can('pos-app.payment.cash') && (
                <div className='col'>
                    <button className='large-blue-outline-sq-btn w-100' onClick={() => openCashPayment(getTotal())}>
                        <svg width="22" height="22" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '5px' }}>
                            <path d="M9.625 17.3333C9.625 18.9442 10.9308 20.25 12.5417 20.25H15.25C16.9759 20.25 18.375 18.8509 18.375 17.125C18.375 15.3991 16.9759 14 15.25 14H12.75C11.0241 14 9.625 12.6009 9.625 10.875C9.625 9.14911 11.0241 7.75 12.75 7.75H15.4583C17.0692 7.75 18.375 9.05584 18.375 10.6667M14 5.875V7.75M14 20.25V22.125M26.5 14C26.5 20.9036 20.9036 26.5 14 26.5C7.09644 26.5 1.5 20.9036 1.5 14C1.5 7.09644 7.09644 1.5 14 1.5C20.9036 1.5 26.5 7.09644 26.5 14Z" stroke="#1643E0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        Cash
                    </button>
                </div>
                )}
                {can('pos-app.payment.card') && (
                <div className='col'>
                    <button className='large-blue-outline-sq-btn w-100' onClick={() => openCardPayment(getTotal())}>
                        <svg width="22" height="22" viewBox="0 0 28 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '5px' }}>
                            <path d="M26.5 7.5H1.5M1.5 5.25L1.5 14.75C1.5 16.1501 1.5 16.8502 1.77248 17.385C2.01217 17.8554 2.39462 18.2378 2.86502 18.4775C3.3998 18.75 4.09987 18.75 5.5 18.75L22.5 18.75C23.9001 18.75 24.6002 18.75 25.135 18.4775C25.6054 18.2378 25.9878 17.8554 26.2275 17.385C26.5 16.8502 26.5 16.1501 26.5 14.75V5.25C26.5 3.84987 26.5 3.1498 26.2275 2.61503C25.9878 2.14462 25.6054 1.76217 25.135 1.52249C24.6002 1.25 23.9001 1.25 22.5 1.25L5.5 1.25C4.09987 1.25 3.3998 1.25 2.86502 1.52248C2.39462 1.76217 2.01217 2.14462 1.77248 2.61502C1.5 3.1498 1.5 3.84987 1.5 5.25Z" stroke="#1643E0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        Card
                    </button>
                </div>
                )}
                {onChargeOrderToRoom && can('pos-app.payment.room-charge') && (
                <div className='col'>
                    <button className='large-blue-outline-sq-btn w-100' onClick={openRoomCharge}>
                        <svg width="23" height="22" viewBox="0 0 30 22" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '5px' }}>
                            <path d="M29.0625 16.1562H28.5938V12.4062C28.5931 11.9092 28.3953 11.4326 28.0439 11.0811C27.6924 10.7297 27.2158 10.5319 26.7188 10.5312V2.5625C26.7181 2.06542 26.5203 1.58888 26.1689 1.23739C25.8174 0.885904 25.3408 0.688151 24.8438 0.6875H5.15625C4.65917 0.688151 4.18263 0.885904 3.83114 1.23739C3.47965 1.58888 3.2819 2.06542 3.28125 2.5625V10.5312C2.78417 10.5319 2.30763 10.7297 1.95614 11.0811C1.60465 11.4326 1.4069 11.9092 1.40625 12.4062V16.1562H0.9375C0.81318 16.1562 0.693951 16.2056 0.606044 16.2935C0.518136 16.3815 0.46875 16.5007 0.46875 16.625V18.5C0.46875 18.6243 0.518136 18.7435 0.606044 18.8315C0.693951 18.9194 0.81318 18.9688 0.9375 18.9688H1.40625V20.8438C1.40625 20.9681 1.45564 21.0873 1.54354 21.1752C1.63145 21.2631 1.75068 21.3125 1.875 21.3125H3.28125C3.38959 21.3126 3.49459 21.275 3.57836 21.2063C3.66213 21.1376 3.71947 21.042 3.74062 20.9357L4.13437 18.9688H25.8656L26.2594 20.9357C26.2805 21.042 26.3379 21.1376 26.4216 21.2063C26.5054 21.275 26.6104 21.3126 26.7188 21.3125H28.125C28.2493 21.3125 28.3685 21.2631 28.4565 21.1752C28.5444 21.0873 28.5938 20.9681 28.5938 20.8438V18.9688H29.0625C29.1868 18.9688 29.306 18.9194 29.394 18.8315C29.4819 18.7435 29.5312 18.6243 29.5312 18.5V16.625C29.5312 16.5007 29.4819 16.3815 29.394 16.2935C29.306 16.2056 29.1868 16.1562 29.0625 16.1562ZM4.21875 2.5625C4.21908 2.31396 4.31795 2.07569 4.4937 1.89995C4.66944 1.7242 4.90771 1.62533 5.15625 1.625H24.8438C25.0923 1.62533 25.3306 1.7242 25.5063 1.89995C25.682 2.07569 25.7809 2.31396 25.7812 2.5625V10.5312H24.8438V8.65625C24.8431 8.15917 24.6453 7.68263 24.2939 7.33114C23.9424 6.97965 23.4658 6.7819 22.9688 6.78125H17.3438C16.8467 6.7819 16.3701 6.97965 16.0186 7.33114C15.6672 7.68263 15.4694 8.15917 15.4688 8.65625V10.5312H14.5312V8.65625C14.5306 8.15917 14.3328 7.68263 13.9814 7.33114C13.6299 6.97965 13.1533 6.7819 12.6562 6.78125H7.03125C6.53417 6.7819 6.05763 6.97965 5.70614 7.33114C5.35465 7.68263 5.1569 8.15917 5.15625 8.65625V10.5312H4.21875V2.5625ZM23.9062 8.65625V10.5312H16.4062V8.65625C16.4066 8.40771 16.5055 8.16944 16.6812 7.9937C16.8569 7.81795 17.0952 7.71908 17.3438 7.71875H22.9688C23.2173 7.71908 23.4556 7.81795 23.6313 7.9937C23.807 8.16944 23.9059 8.40771 23.9062 8.65625ZM13.5938 8.65625V10.5312H6.09375V8.65625C6.09408 8.40771 6.19295 8.16944 6.3687 7.9937C6.54444 7.81795 6.78271 7.71908 7.03125 7.71875H12.6562C12.9048 7.71908 13.1431 7.81795 13.3188 7.9937C13.4945 8.16944 13.5934 8.40771 13.5938 8.65625ZM2.34375 12.4062C2.34408 12.1577 2.44295 11.9194 2.6187 11.7437C2.79444 11.568 3.03271 11.4691 3.28125 11.4688H26.7188C26.9673 11.4691 27.2056 11.568 27.3813 11.7437C27.557 11.9194 27.6559 12.1577 27.6562 12.4062V16.1562H2.34375V12.4062ZM2.89687 20.375H2.34375V18.9688H3.17813L2.89687 20.375ZM27.6562 20.375H27.1031L26.8219 18.9688H27.6562V20.375ZM28.5938 18.0312H1.40625V17.0938H28.5938V18.0312Z" fill="#1643E0" />
                        </svg>
                        Charge to Room
                    </button>
                </div>
                )}
                </div>
            </div>
            )}
        </div>
      </>
    );
};

export default ReceiptPopup;

